import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedPurchaseButton } from '../PdpSharedButtons/PdpSharedPurchaseButton';
import { PdpSharedAuctionMessageContainer } from './PdpSharedAuctionMessageContainer';
import { PdpSharedBuyItNowButton } from '../shared/PdpSharedShoppingButtons/PdpSharedBuyItNowButton';

// types
import { PdpSharedAuctionSecondChanceOffer_item$key as Item } from './__generated__/PdpSharedAuctionSecondChanceOffer_item.graphql';
import { PdpSharedAuctionSecondChanceOffer_user$key } from './__generated__/PdpSharedAuctionSecondChanceOffer_user.graphql';
import { PdpSharedAuctionSecondChanceOffer_viewer$key } from './__generated__/PdpSharedAuctionSecondChanceOffer_viewer.graphql';

// styles
import styles from './PdpSharedAuctionSecondChanceOffer.scss';

const viewerFragment = graphql`
    fragment PdpSharedAuctionSecondChanceOffer_viewer on Viewer {
        isMultiItemCart: featureFlag(feature: "multiItemCart")
    }
`;

const userFragment = graphql`
    fragment PdpSharedAuctionSecondChanceOffer_user on User {
        ...PdpSharedBuyItNowButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpSharedAuctionSecondChanceOffer_item on Item {
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...PdpSharedPurchaseButton_item
        ...PdpSharedBuyItNowButton_item
    }
`;

type RelayVariables = {
    userId: string;
    itemId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

type Props = {
    viewer: PdpSharedAuctionSecondChanceOffer_viewer$key;
    user: PdpSharedAuctionSecondChanceOffer_user$key | null;
    item: Item;
    currency: string;
};

export const PdpSharedAuctionSecondChanceOffer: FC<Props> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);
    const { isMultiItemCart } = viewer;

    const { userId, itemId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );

    return (
        <div className={styles.container}>
            <PdpSharedAuctionMessageContainer
                isFullWidth
                text={
                    <FormattedMessage
                        id="PdpSharedAuctionSecondChanceOffer.secondChanceOffer"
                        defaultMessage="You have a limited-time offer from the seller."
                    />
                }
            />
            <div className={styles.price}>
                <PriceDisplay currency={currency} item={item} useSvgIcon sku={null} />
            </div>
            <div className={styles.purchase}>
                {isMultiItemCart ? (
                    <PdpSharedBuyItNowButton variant="auction" user={user} item={item} />
                ) : (
                    <PdpSharedPurchaseButton
                        item={item}
                        itemId={itemId}
                        userId={userId}
                        quantitySelected="1"
                    />
                )}
            </div>
        </div>
    );
};
